import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import Backdrop from '@mui/material/Backdrop';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import { DialogContext } from '../../hooks/DialogContext';
import { FileUploadProgressContext } from '../../scenes/ProjectFiles/ProjectFiles';
import UploadForm from './UploadForm/UploadForm';

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.black,
    zIndex: theme.zIndex.drawer
}));

const Dropzone = ({ isOpen, onClose }) => {
    const [isProhibitedType, setIsProhibitedType] = useState(false);
    const [file, setFile] = useState(null);
    const { openDialog } = useContext(DialogContext);
    const { projectId } = useParams();
    const { path } = useRouteMatch();
    const isPrivateFolder = path.search(/private-folder/) > 0;

    // Get progress callback from context, with fallback
    const progressCallback = useContext(FileUploadProgressContext) || { setIsUploading: () => {} };

    const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/svg+xml',
        'image/jpeg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    useEffect(() => {
        if (file) {
            console.log('Dropzone uploading file', file);

            openDialog({
                isOpen: true,
                status: null,
                title: 'File uploading form',
                content: <UploadForm fileData={file} projectId={projectId} isPrivateFolder={isPrivateFolder} progressCallback={progressCallback} />
            });
            setFile(null);
            onClose();
        }
    }, [file, openDialog, projectId, isPrivateFolder, onClose, progressCallback]);

    // Handle file input change (when user selects file via dialog)
    const onFileInputChange = (e) => {
        e.preventDefault();
        const selectedFile = e.target.files[0];

        if (selectedFile) {
            if (!allowedTypes.includes(selectedFile.type)) {
                setIsProhibitedType(true);
            } else {
                setFile(selectedFile);
            }
        }
    };

    // Handle drag over
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isProhibitedType) {
            setIsProhibitedType(false);
        }
    };

    // Handle drag enter
    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Handle drag leave
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Handle drop
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            const droppedFile = droppedFiles[0];

            if (!allowedTypes.includes(droppedFile.type)) {
                setIsProhibitedType(true);
            } else {
                setFile(droppedFile);
            }
        }
    };

    const onClick = (e) => {
        e.preventDefault();
        onClose();
    };

    return (
        <>
            <StyledBackdrop
                open={isOpen}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
            >
                {
                    !isProhibitedType
                    && (
                        <>
                            <ArrowCircleDownIcon sx={{ width: '80px', height: '80px', color: 'common.white' }} />
                            <Typography color="common.white" align="center" sx={{ mt: '1rem' }}>Drop here</Typography>
                        </>
                    )
                }
                {
                    isProhibitedType
                        && (
                            <>
                                <CancelIcon sx={{ width: '80px', height: '80px', color: 'common.white' }} />
                                <Typography color="common.white" align="center" sx={{ mt: '1rem' }}>File type is not allowed</Typography>
                            </>
                        )
                }
            </StyledBackdrop>
            <input
                type="file"
                value=""
                onChange={onFileInputChange}
                onClick={onClick}
                style={{ position: 'absolute', top: 0, left: 0, opacity: 0, zIndex: '1200', width: '100vw', height: '100vh' }}
            />
        </>
    );
};

Dropzone.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default Dropzone;
